import Api from '../../services/Api.ts';
import { useEffect, useState } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress } from '@mui/material';

interface OfferContent {
  title: string;
  subtitle: string;
  offerAmount?: number;
}

interface DriverConfigValue {
  offers?: {
    enabled: boolean;
    multilingual_content: {
      [lang: string]: OfferContent & { description?: string; timer?: { startTime: string; endTime: string } };
    };
  };
  [key: string]: any;
}

const DriverConfig = () => {
  const [config, setConfig] = useState<DriverConfigValue | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Api.get('/driver-config')
      .then((res) => {
        const items = res.data?.data || [];
        const driverConfigItem = items.find((item: any) => item.key === 'driver_config');
        if (driverConfigItem && driverConfigItem.value) {
          setConfig(JSON.parse(driverConfigItem.value));
        } else {
          setError('No driver_config found');
        }
        setLoading(false);
      })
      .catch(() => {
        setError('Failed to fetch config');
        setLoading(false);
      });
  }, []);

  if (loading) return <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>;
  if (error) return <Typography color="error">{error}</Typography>;

  // If config has a single root key (like driver_config), use its value
  if (!config) return null;
  let configObj = config;
  const configKeys = Object.keys(config);
  if (configKeys.length === 1 && typeof config[configKeys[0]] === 'object') {
    configObj = (config as any)[configKeys[0]];
  }
  if (!configObj) return null;

  // Helper to render multilingual labels
  const renderMultilingualLabels = (labels: any) => {
    if (!labels || typeof labels !== 'object') return '';
    return (
      <Table size="small" sx={{ mb: 1, background: '#fafafa' }}>
        <TableHead>
          <TableRow>
            <TableCell>Lang</TableCell>
            <TableCell>Labels</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.entries(labels).map(([lang, values]) => (
            <TableRow key={lang}>
              <TableCell>{lang}</TableCell>
              <TableCell>{typeof values === 'object' && values !== null ? Object.entries(values).map(([k, v]) => `${k}: ${v}`).join(', ') : String(values)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };


  const renderArray = (arr: any[]) => arr.map((item) => (typeof item === 'object' ? JSON.stringify(item) : String(item))).join(', ');

  const extractFieldColumns = (field: any) => {
    if (typeof field !== 'object' || field === null) {
      return { type: '', required: '', options: '', value: String(field), labels: '' };
    }
    const { type, required, options, multilingual_labels, ...rest } = field;
    let value = '';
    if (type || required !== undefined || options || multilingual_labels) {
      value = Object.keys(rest).length > 0 ? JSON.stringify(rest, null, 2) : '';
    } else if (Array.isArray(field)) {
      value = renderArray(field);
    } else {
      value = String(field);
    }
    return {
      type: type || '',
      required: required !== undefined ? String(required) : '',
      options: options ? (Array.isArray(options) ? renderArray(options) : JSON.stringify(options)) : '',
      value,
      labels: multilingual_labels ? renderMultilingualLabels(multilingual_labels) : ''
    };
  };

  const renderFields = (obj: any, parentKey?: string) => {
    if (parentKey === 'offers' && obj.multilingual_content) {
      const offers = obj.multilingual_content;
      return (
        <Box sx={{ p: 2 }}>
          <div style={{ marginBottom: 8 }}><b>enabled:</b> {String(obj.enabled)}</div>
          <div style={{ marginBottom: 8 }}><b>multilingual_content:</b></div>
          <Table size="small" sx={{ mb: 2, background: '#fafafa' }}>
            <TableHead>
              <TableRow>
                <TableCell>Lang</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Subtitle</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Timer</TableCell>
                <TableCell>Offer Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(offers).map(([lang, offer]: any) => (
                <TableRow key={lang}>
                  <TableCell>{lang}</TableCell>
                  <TableCell>{offer.title}</TableCell>
                  <TableCell>{offer.subtitle}</TableCell>
                  <TableCell>{offer.description}</TableCell>
                  <TableCell>{offer.timer ? `${offer.timer.startTime} - ${offer.timer.endTime}` : ''}</TableCell>
                  <TableCell>{offer.offerAmount ?? ''}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      );
    }

    if (parentKey === 'registration') {
      return (
        <Table
          size="small"
          sx={{
            mb: 4,
            background: '#f9f9f9',
            border: '1px solid #e0e0e0',
            '& .MuiTableCell-root': {
              border: '1px solid #e0e0e0',
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Field</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Required</TableCell>
              <TableCell>Options</TableCell>
              <TableCell>Labels</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(obj).map(([key, value]) => {
              const { type, required, options, labels } = extractFieldColumns(value);
              return (
                <TableRow key={key}>
                  <TableCell sx={{ fontWeight: 600 }}>{key}</TableCell>
                  <TableCell>{type}</TableCell>
                  <TableCell>{required}</TableCell>
                  <TableCell>{options}</TableCell>
                  <TableCell>{labels}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      );
    }

    return (
      <Table
        size="small"
        sx={{
          mb: 4,
          background: '#f9f9f9',
          border: '1px solid #e0e0e0',
          '& .MuiTableCell-root': {
            border: '1px solid #e0e0e0',
          },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Field</TableCell>
            <TableCell>Value</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.entries(obj).map(([key, value]) => (
            <TableRow key={key}>
              <TableCell sx={{ fontWeight: 600 }}>{key}</TableCell>
              <TableCell>{typeof value === 'object' ? JSON.stringify(value) : String(value)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  const offers = configObj.offers?.multilingual_content || {};

  return (
    <Box p={3}>
      <Typography variant="h5" gutterBottom>Driver Config</Typography>
      {Object.entries(configObj).map(([section, value]) => (
        section === 'metadata' ? null : (
          <Box key={section} mb={4}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>{section.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</Typography>
            {typeof value === 'object' && value !== null && !Array.isArray(value) && Object.keys(value).length > 0 ? (
              <TableContainer component={Paper} sx={{ mb: 2 }}>
                {renderFields(value, section)}
              </TableContainer>
            ) : Array.isArray(value) ? (
              <div>{renderArray(value)}</div>
            ) : (
              <div>-</div>
            )}
          </Box>
        )
      ))}
    </Box>
  );
};

export default DriverConfig;
